/**
 * Clean Mornings (CEM) — as constantes do produto, num lugar só.
 *
 * A VSL v1.2 (25/09/2026) é a fonte: "Real Breakfasts & 3 O'Clock Snacks for
 * Diabetics", narradora Dana Merritt (suposição do redator; troca aqui),
 * mecanismo The Diner Dough, seis grupos "sorted the way you actually eat",
 * três bônus. Nada de preço no app (decisão do dono).
 *
 * Tudo que outra tela precisa saber sobre o CEM sai daqui: o prefixo das
 * rotas, o módulo, as categorias e a ordem delas, as formas da massa, os
 * nomes. Ninguém escreve "/clean-mornings" na mão em outro arquivo.
 */
import { Croissant, Egg, Flame, Salad, Sandwich, Sun, type LucideIcon } from "lucide-react";

export const CEM_PREFIX = "/clean-mornings";

/** id fixo do módulo em recipe_modules (migration 20260925). */
export const CEM_MODULE_ID = "ce200000-0000-4000-8000-000000000001";

/** ID do produto na Hotmart (front do funil Clean Mornings, criado em 25/09/2026). */
export const CEM_PRODUCT_ID = "8597307";

export const CEM_NOME = "Clean Mornings";
/**
 * A IA dentro do Clean Mornings fala como a Dana. A função natasha-chef
 * responde com persona "dana" direto no Gemini (supabase/functions/_shared/dana.ts,
 * segredo GEMINI_API_KEY) ou, se N8N_DANA_WEBHOOK_URL existir, pelo fluxo
 * n8n/dana-clean-mornings.json. Ligada, a tela da receita mostra "Ask Dana" e a
 * troca de ingrediente na voz dela; desligada, a receita fica só com o texto.
 */
export const CEM_IA_ATIVA = true;
export const PERSONA_CEM = "dana" as const;
/** A foto da oferta (public/ofertas): capa em Meus conteúdos e abertura da home do CEM. */
export const FOTO_OFERTA_CEM = "/ofertas/clean-mornings.webp";
export const CEM_SUBTITULO = "Real Breakfasts & 3 O'Clock Snacks for Diabetics";

/** A narradora. Nome é suposição da VSL; trocar é só aqui. */
export const NARRADORA = {
  nome: "Dana",
  nomeCompleto: "Dana Merritt",
  /** A credencial em uma linha (VSL, bloco 11). */
  credencial: "Twenty-six years on the breakfast line, type 2 since fifty-two.",
};

export const MECANISMO = "The Diner Dough";

export type CategoriaCEM = "eggs" | "sandwiches" | "burritos" | "sunday" | "bowls" | "snacks";

/** Os seis grupos da VSL (bloco 12), nesta ordem. `valor` é o que está em recipes.category. */
export const CATEGORIAS_CEM: { valor: CategoriaCEM; rotulo: string; linha: string; icone: LucideIcon }[] = [
  { valor: "eggs", rotulo: "Eggs that don't taste like egg", linha: "Loaded omelets, Denver, frittata, creamy scrambled", icone: Egg },
  { valor: "sandwiches", rotulo: "The breakfast sandwich", linha: "Back. Sausage, ham or bacon, egg and cheese — on the Diner Dough", icone: Sandwich },
  { valor: "burritos", rotulo: "Burritos, tacos & quesadillas", linha: "Ten burritos on one sheet pan, into the freezer", icone: Flame },
  { valor: "sunday", rotulo: "Sunday, for the whole week", linha: "Egg bites, breakfast cups, the casserole that feeds a house", icone: Sun },
  { valor: "bowls", rotulo: "Bowls & skillets", linha: "Avocado, eggs and sausage in one bowl or one skillet", icone: Salad },
  { valor: "snacks", rotulo: "The 3 o'clock snack", linha: "Sixty of them, so you never eat the same string cheese twice", icone: Croissant },
];

export const categoriaCEM = (valor: string | null | undefined) =>
  CATEGORIAS_CEM.find((c) => c.valor === valor) ?? null;

export type FormaDaMassa = "english-muffin" | "biscuit" | "bagel" | "tortilla";

/** As quatro formas da massa (VSL blocos 4, 11, 12) + a massa-mãe (`dough_shape = "base"`). */
export const FORMAS_DA_MASSA: { valor: FormaDaMassa; rotulo: string; linha: string }[] = [
  { valor: "english-muffin", rotulo: "English muffin", linha: "The hero. Crust outside, soft inside" },
  { valor: "biscuit", rotulo: "Biscuit", linha: "Split and toasted" },
  { valor: "bagel", rotulo: "Bagel", linha: "Griddled. No yeast, no rising" },
  { valor: "tortilla", rotulo: "Tortilla", linha: "Rolled thin, for burritos and tacos" },
];

export const formaDaMassa = (valor: string | null | undefined) =>
  FORMAS_DA_MASSA.find((f) => f.valor === valor) ?? null;

/** Tags que marcam os bônus de receita e os conjuntos especiais. */
export const TAG_COFFEE_BAR = "coffee-bar";
export const TAG_MAKE_AHEAD_SUNDAY = "make-ahead-sunday";

/** Uma receita é "nova" (selo New) nos primeiros 14 dias. */
export const DIAS_DE_NOVA = 14;

export const ehRotaCEM = (pathname: string) => pathname === CEM_PREFIX || pathname.startsWith(CEM_PREFIX + "/");

export const rotaCategoriaCEM = (categoria: CategoriaCEM) => `${CEM_PREFIX}/recipes/${categoria}`;
